import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useRetroRealtime } from '../../hooks/useRetroRealtime';
import { api } from '../../utils/api';
import RetroCard from './RetroCard';
import CardInput from './CardInput';
import RetroTimer from './RetroTimer';
import ActionPanel from '../actions/ActionPanel';
import ReEstimatePanel from './ReEstimatePanel';
import AllInOneBoard from './AllInOneBoard';
import FacilitatorNotes from './FacilitatorNotes';
import TutorialModal from './TutorialModal';
import {
  Users, Share2, ChevronDown, ChevronUp, Copy, CheckCircle,
  Lock, Unlock, Eye, SkipForward, XCircle, RefreshCw,
  BarChart2, Download, Bookmark, AlertTriangle, UserCheck
} from 'lucide-react';

// PHASES is now dynamically loaded from the DB (see phases state below)

const PHASE_META = {
  lobby:      { label: 'Lobby',        emoji: '🏠', color: 'var(--text-muted)' },
  start:      { label: 'Start Doing',  emoji: '🟢', color: 'var(--phase-start)' },
  stop:       { label: 'Stop Doing',   emoji: '🔴', color: 'var(--phase-stop)' },
  continue:   { label: 'Continue',     emoji: '🔵', color: 'var(--phase-continue)' },
  kudos:      { label: 'Kudos Wall',   emoji: '🏆', color: 'var(--phase-kudos)' },
  discussion: { label: 'Discussion',   emoji: '💬', color: 'var(--accent)' },
  voting:     { label: 'Voting',       emoji: '🗳️', color: 'var(--accent)' },
  complete:   { label: 'Complete',     emoji: '✅', color: 'var(--green)' }
};

// WRITING_PHASES is now derived from the dynamic phases state

function PrevPhaseSummary({ cards, phases, isFacilitator, myParticipantId, selectedCards, onSelectForMerge, onUpdated, onDeleted }) {
  const [open, setOpen] = React.useState(false);
  if (!cards.length) return null;
  return (
    <div className="card mt-3" style={{ background: 'var(--bg-elevated)' }}>
      <button className="btn btn-ghost btn-sm w-full" style={{ justifyContent: 'space-between', padding: 0 }} onClick={() => setOpen(o => !o)}>
        <span className="text-xs font-bold text-muted">📋 Previous phases ({cards.length} cards)</span>
        <span className="text-xs text-muted">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className="mt-3 flex-col gap-3">
          {phases.map(p => {
            const pCards = cards.filter(c => c.phase === p);
            if (!pCards.length) return null;
            const meta = PHASE_META[p];
            return (
              <div key={p}>
                <div className="text-xs font-bold mb-2" style={{ color: meta.color }}>{meta.emoji} {meta.label}</div>
                <div className="flex-col gap-2">
                  {pCards.map(c => (
                    <RetroCard key={c.id} card={c} phase={p} isFacilitator={isFacilitator}
                      myParticipantId={myParticipantId} isRevealed={true} isLocked={false}
                      selectedForMerge={selectedCards ? selectedCards.has(c.id) : false}
                      onSelectForMerge={onSelectForMerge || (() => {})}
                      onUpdated={onUpdated || (() => {})}
                      onDeleted={onDeleted || (() => {})}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function SessionPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [session, setSession] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [cards, setCards] = useState([]);
  const [actions, setActions] = useState([]);
  const [myVotes, setMyVotes] = useState([]);
  const [allVotes, setAllVotes] = useState([]);
  const [myParticipantId, setMyParticipantId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [showParticipants, setShowParticipants] = useState(true);
  const [showReEstimate, setShowReEstimate] = useState(false);
  const [guidanceOpen, setGuidanceOpen] = useState(() => localStorage.getItem(`retro_guidance_${sessionId}`) !== 'closed');
  const [mergeSelection, setMergeSelection] = useState([]);
  const [mergeLabel, setMergeLabel] = useState('');
  const [showMergeInput, setShowMergeInput] = useState(false);
  const [selectedCards, setSelectedCards] = useState(new Set());
  const [phaseLoading, setPhaseLoading] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [phases, setPhases] = useState(['lobby','start','stop','continue','kudos','discussion','voting','complete']);
  const [nameInput, setNameInput] = useState('');

  const isFacilitator = user && session?.facilitator_jira_id === user.jiraId;

  const loadSession = useCallback(async () => {
    try {
      const data = await api.sessions.get(sessionId);
      setSession(data);
      setParticipants(data.participants || []);
      setCards(data.cards || []);
      setActions(data.actions || []);

      const pid = localStorage.getItem(`retro_participant_${sessionId}`);
      if (pid) { setMyParticipantId(pid); }
      else if (user) {
        const myP = (data.participants || []).find(p => p.jira_id === user.jiraId);
        if (myP) {
          localStorage.setItem(`retro_participant_${sessionId}`, myP.id);
          setMyParticipantId(myP.id);
        } else if (data.status === 'active') {
          // Auto-join: authenticated user who bypassed the join page
          try {
            const joinRes = await api.sessions.join(sessionId, { display_name: user.displayName, anonymous_id: user.jiraId });
            const newP = joinRes.participant;
            localStorage.setItem(`retro_participant_${sessionId}`, newP.id);
            setMyParticipantId(newP.id);
          } catch { /* ignore if already joined or session not joinable */ }
        }
      }
    } catch (e) { setError('Session not found.'); }
    finally { setLoading(false); }
  }, [sessionId, user]);

  const loadVotes = useCallback(async () => {
    if (!myParticipantId) return;
    try {
      const votes = await api.sessions.votes(sessionId);
      setMyVotes((votes || []).filter(v => v.participant_id === myParticipantId));
      setAllVotes(votes || []);
    } catch { /* ignore */ }
  }, [sessionId, myParticipantId]);

  useEffect(() => {
    const pid = localStorage.getItem(`retro_participant_${sessionId}`);
    if (!pid && !user) { navigate(`/join/${sessionId}`); return; }
    loadSession();
  }, [sessionId]);

  useEffect(() => { if (myParticipantId) loadVotes(); }, [myParticipantId]);

  useEffect(() => {
    if (session?.format_id) {
      api.formats.getPhases(session.format_id)
        .then(formatPhases => {
          const slugs = (formatPhases || []).map(p => p.slug);
          setPhases(['lobby', ...slugs, 'discussion', 'voting', 'complete']);
        })
        .catch(() => {}); // keep default phases on error
    }
  }, [session?.format_id]);

  useRetroRealtime(sessionId, {
    onSession: loadSession,
    onCards: loadSession,
    onActions: loadSession,
    onVotes: () => setTimeout(loadVotes, 600),
    onParticipants: loadSession
  });

  const phase = session?.phase || 'lobby';
  const phaseMeta = PHASE_META[phase] || PHASE_META.lobby;
  const isLocked = session?.phase_locked || false;
  const phaseCards = cards.filter(c => c.phase === phase);
  const writingPhases = phases.filter(p => !['lobby','discussion','voting','complete'].includes(p));
  const isWritingPhase = writingPhases.includes(phase);

  // Group cards for display
  const groupCards = (phaseCards) => {
    const groups = {};
    const ungrouped = [];
    phaseCards.forEach(c => {
      if (c.group_id && c.group_id !== c.id) {
        if (!groups[c.group_id]) groups[c.group_id] = [];
        groups[c.group_id].push(c);
      } else if (c.group_id === c.id) {
        if (!groups[c.id]) groups[c.id] = [];
        groups[c.id].unshift(c); // anchor first
      } else {
        ungrouped.push(c);
      }
    });
    return { groups, ungrouped };
  };

  const advancePhase = async () => {
    if (phaseLoading) return;
    const idx = phases.indexOf(phase);
    if (idx < phases.length - 1) {
      setPhaseLoading(true);
      try {
        if (writingPhases.includes(phase)) {
          const unrevealed = phaseCards.filter(c => !c.is_revealed);
          if (unrevealed.length > 0) await api.sessions.reveal(sessionId);
        }
        await api.sessions.phase(sessionId, phases[idx + 1]);
        await loadSession();
      } finally {
        setPhaseLoading(false);
      }
    }
  };

  const goBackPhase = async () => {
    if (phaseLoading) return;
    const idx = phases.indexOf(phase);
    if (idx > 0) {
      setPhaseLoading(true);
      try {
        await api.sessions.phase(sessionId, phases[idx - 1]);
        await loadSession();
      } finally {
        setPhaseLoading(false);
      }
    }
  };

  const handleReveal = async () => { await api.sessions.reveal(sessionId); };
  const handleLockToggle = async () => { await api.sessions.lock(sessionId, !isLocked); };
  const handleTimer = async (action) => { await api.sessions.timer(sessionId, action); };
  const handleEnd = async () => {
    if (!window.confirm('End this session?')) return;
    await api.sessions.end(sessionId);
  };

  const toggleMergeCard = (cardId) => {
    setSelectedCards(prev => {
      const next = new Set(prev);
      if (next.has(cardId)) next.delete(cardId); else next.add(cardId);
      return next;
    });
  };

  const doMerge = async () => {
    if (selectedCards.size < 2) return;
    await api.cards.merge({ card_ids: [...selectedCards], group_label: mergeLabel || null, session_id: sessionId });
    setSelectedCards(new Set()); setMergeLabel(''); setShowMergeInput(false);
    loadSession();
  };

  const handleNameSave = async () => {
    if (!nameInput.trim() || !myParticipantId) return;
    try {
      await api.sessions.updateParticipantName(sessionId, myParticipantId, { display_name: nameInput.trim() });
      setEditingName(false);
      loadSession();
    } catch (e) { console.error(e); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/join/${sessionId}`);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  const transferFacilitator = async (participantId) => {
    if (!window.confirm('Transfer facilitator role to this participant?')) return;
    await api.sessions.transferFacilitator(sessionId, participantId);
    loadSession();
  };

  if (loading) return <div className="page flex items-center justify-center" style={{ minHeight: '60vh' }}><div style={{ textAlign: 'center' }}><div style={{ fontSize: '3rem' }} className="pulse">🔄</div><p className="text-muted mt-4">Loading session...</p></div></div>;
  if (!session) return <div className="page flex items-center justify-center" style={{ minHeight: '60vh' }}><div className="card" style={{ textAlign: 'center' }}><p className="text-muted">{error || 'Session not found.'}</p><button className="btn btn-primary mt-4" onClick={() => navigate('/')}>Go Home</button></div></div>;

  const { groups, ungrouped } = groupCards(phaseCards);

  return (
    <div className="page" style={{ maxWidth: 1400 }}>
      {/* Session header */}
      <div className="flex items-center gap-4 mb-6" style={{ flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="flex items-center gap-3 mb-1 flex-wrap">
            <h1 style={{ fontSize: '1.4rem' }}>{session.name}</h1>
            <span className="badge badge-green">Live</span>
            <span className="badge" style={{ background: `color-mix(in srgb, ${phaseMeta.color} 15%, transparent)`, color: phaseMeta.color }}>
              {phaseMeta.emoji} {phaseMeta.label}
            </span>
            {isLocked && <span className="badge badge-red"><Lock size={9} /> Locked</span>}
          </div>
          <p className="text-sm text-muted">{session.teams?.name} · {session.facilitator_name}</p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button className="btn btn-secondary btn-sm" onClick={copyLink}>
            {copied ? <><CheckCircle size={14} /> Copied!</> : <><Share2 size={14} /> Share</>}
          </button>
          {isFacilitator && (
            <>
              {isWritingPhase && (
              <button className="btn btn-secondary btn-sm" onClick={handleLockToggle}>
                {isLocked ? <><Unlock size={14} /> Unlock</> : <><Lock size={14} /> Lock</>}
              </button>
              )}
              {isWritingPhase && !isLocked && (
                <button className="btn btn-primary btn-sm" onClick={handleReveal}>👁 Reveal Cards</button>
              )}
              {phase !== 'lobby' && phase !== 'complete' && (
                <button className="btn btn-ghost btn-sm" onClick={goBackPhase} title="Go back one phase">◀ Back</button>
              )}
              {phase !== 'complete' && (
                <button className="btn btn-primary btn-sm" onClick={advancePhase} disabled={phaseLoading}>
                  {phaseLoading ? '⏳' : phase === 'lobby' ? '▶ Start' : <><SkipForward size={14} /> Next Phase</>}
                </button>
              )}
              <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }} onClick={handleEnd}>
                <XCircle size={14} /> End
              </button>
            </>
          )}
        </div>
      </div>

      {/* Phase progress bar */}
      <div className="flex items-center gap-1 mb-6" style={{ flexWrap: 'wrap' }}>
        {phases.filter(p => p !== 'lobby').map((p, i) => {
          const idx = phases.indexOf(phase);
          const pIdx = phases.indexOf(p);
          const isDone = pIdx < idx;
          const isCurrent = p === phase;
          const meta = PHASE_META[p] || { label: p, emoji: '📋', color: 'var(--text-muted)' };
          return (
            <React.Fragment key={p}>
              <div
                onClick={isDone && isFacilitator ? () => api.sessions.phase(sessionId, p).then(loadSession) : undefined}
                style={{
                  padding: '4px 10px', borderRadius: 100, fontSize: 11, fontWeight: 600,
                  background: isCurrent ? `color-mix(in srgb, ${meta.color} 20%, transparent)` : isDone ? 'var(--bg-elevated)' : 'transparent',
                  color: isCurrent ? meta.color : isDone ? 'var(--text-secondary)' : 'var(--text-muted)',
                  border: isCurrent ? `1px solid ${meta.color}` : '1px solid transparent',
                  transition: 'all 0.2s',
                  cursor: isDone && isFacilitator ? 'pointer' : 'default'
                }}>
                {meta.emoji} {meta.label}
              </div>
              {i < phases.length - 2 && <div style={{ width: 12, height: 1, background: 'var(--border)' }} />}
            </React.Fragment>
          );
        })}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 24, alignItems: 'start' }}>
        {/* Main board */}
        <div>
          {/* Timer */}
          {isWritingPhase && (
            <div className="card mb-4" style={{ padding: '16px 20px' }}>
              <div className="flex items-center justify-between">
                <RetroTimer
                  totalSeconds={session.timer_seconds}
                  startedAt={session.timer_started_at}
                  pausedAt={session.timer_paused_at}
                  extraSeconds={session.timer_extra_seconds}
                  isFacilitator={isFacilitator}
                  isLocked={isLocked}
                  onAction={handleTimer}
                />
                <div className="text-sm text-muted">{phaseCards.length} card{phaseCards.length !== 1 ? 's' : ''}</div>
              </div>
            </div>
          )}

          {/* LOBBY phase */}
          {phase === 'lobby' && (
            <div className="card" style={{ textAlign: 'center', padding: '48px 32px' }}>
              <div style={{ fontSize: '3rem', marginBottom: 16 }}>🔄</div>
              <h2 style={{ marginBottom: 8 }}>Waiting to start</h2>
              <p className="text-muted mb-6">Share the link with your team, then start when everyone is in.</p>
              <button className="btn btn-secondary" onClick={copyLink}><Copy size={14} /> {copied ? 'Copied!' : 'Copy invite link'}</button>
              {isFacilitator && <div className="mt-4"><button className="btn btn-primary btn-lg" onClick={advancePhase}>▶ Start Retro</button></div>}
            </div>
          )}

          {/* WRITING phases — all-in-one mode routes to AllInOneBoard */}
          {isWritingPhase && session?.mode === 'all_in_one' && (
            <AllInOneBoard
              sessionId={sessionId}
              session={session}
              phases={writingPhases}
              isFacilitator={isFacilitator}
              myParticipantId={myParticipantId}
              isLocked={isLocked}
              onAdvance={advancePhase}
            />
          )}

          {/* WRITING phases */}
          {isWritingPhase && session?.mode !== 'all_in_one' && (
            <div>
              <div className="card mb-4" style={{ borderColor: phaseMeta.color }}>
                <div className="flex items-center gap-3 mb-2">
                  <span style={{ fontSize: '1.4rem' }}>{phaseMeta.emoji}</span>
                  <div>
                    <h2 style={{ fontSize: '1.2rem', color: phaseMeta.color }}>{phaseMeta.label}</h2>
                    <p className="text-xs text-muted">
                      {phase === 'start' && 'What should we start doing to improve?'}
                      {phase === 'stop' && 'What should we stop doing that is not helping?'}
                      {phase === 'continue' && 'What is working well that we should keep doing?'}
                      {phase === 'kudos' && 'Recognise someone. Celebrate the team.'}
                    </p>
                  </div>
                </div>

                {/* Estimation guidance */}
                <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', marginBottom: 12 }}>
                  <button className="btn btn-ghost btn-sm w-full" style={{ justifyContent: 'space-between', padding: '8px 12px', borderRadius: 0 }}
                    onClick={() => {
                      const next = !guidanceOpen;
                      setGuidanceOpen(next);
                      localStorage.setItem(`retro_guidance_${sessionId}`, next ? 'open' : 'closed');
                    }}>
                    <span className="text-xs font-bold">💡 Phase Tips</span>
                    <span className="text-xs text-muted">{guidanceOpen ? '▲' : '▼'}</span>
                  </button>
                  {guidanceOpen && (
                    <div style={{ padding: '8px 14px 10px' }}>
                      <p className="text-xs text-muted" style={{ lineHeight: 1.7 }}>
                        <strong>Write one idea per card.</strong> Be specific — "sprint planning runs long" not "ceremonies are bad."
                        Be honest, be constructive. Focus on the process, not people.
                        {!isLocked && ' Cards are hidden until the facilitator reveals them.'}
                      </p>
                      {phase === 'start' && <p className="text-xs text-muted mt-1" style={{ lineHeight: 1.7 }}>💡 <em>Think about tools, processes, meetings, or habits that would improve velocity or morale.</em></p>}
                      {phase === 'stop' && <p className="text-xs text-muted mt-1" style={{ lineHeight: 1.7 }}>💡 <em>Think about blockers, distractions, or ceremonies that consume time without value.</em></p>}
                      {phase === 'continue' && <p className="text-xs text-muted mt-1" style={{ lineHeight: 1.7 }}>💡 <em>Protect what's working. Consistency in good habits compounds over time.</em></p>}
                      {phase === 'kudos' && <p className="text-xs text-muted mt-1" style={{ lineHeight: 1.7 }}>💡 <em>Recognise a specific action, not just a person. "Great job" is less powerful than "Thanks for unblocking the API issue at 10pm."</em></p>}
                    </div>
                  )}
                </div>

                {!isLocked && (
                  <CardInput
                    sessionId={sessionId} participantId={myParticipantId}
                    phase={phase} isKudos={phase === 'kudos'}
                    onSubmitted={c => setCards(prev => [...prev, c])}
                  />
                )}
                {isLocked && !isFacilitator && (
                  <div className="toast toast-warning mt-2"><Lock size={12} /> Board locked by facilitator</div>
                )}
              </div>

              {/* Cards display */}
              {isLocked && phaseCards.some(c => c.is_revealed) && (
                <div>
                  {/* Merge controls */}
                  {isFacilitator && (
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <span className="text-xs text-muted">
                        {selectedCards.size > 0 ? `${selectedCards.size} selected` : 'Click merge icon on cards to group them'}
                      </span>
                      {selectedCards.size >= 2 && !showMergeInput && (
                        <button className="btn btn-primary btn-sm" onClick={() => setShowMergeInput(true)}>Merge {selectedCards.size} cards</button>
                      )}
                      {selectedCards.size > 0 && (
                        <button className="btn btn-ghost btn-sm" onClick={() => setSelectedCards(new Set())}>Clear</button>
                      )}
                      {showMergeInput && (
                        <div className="flex items-center gap-2">
                          <input className="input" value={mergeLabel} onChange={e => setMergeLabel(e.target.value)}
                            placeholder="Group label (optional)" style={{ width: 180 }} />
                          <button className="btn btn-primary btn-sm" onClick={doMerge}>Confirm Merge</button>
                          <button className="btn btn-ghost btn-sm" onClick={() => { setShowMergeInput(false); setMergeLabel(''); }}>Cancel</button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Group cards */}
                  {Object.entries(groups).map(([groupId, groupCards]) => (
                    <div key={groupId} style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-bright)', borderRadius: 'var(--radius-sm)', padding: '10px', marginBottom: 8 }}>
                      {groupCards[0]?.group_label && (
                        <div className="flex items-center gap-2 mb-2">
                          <span className="badge badge-purple" style={{ fontSize: 10 }}>{groupCards[0].group_label}</span>
                          <span className="text-xs text-muted">{groupCards.length} cards</span>
                          {isFacilitator && (
                            <button className="btn btn-ghost btn-sm text-xs" style={{ padding: '2px 6px' }}
                              onClick={async () => {
                                const lbl = window.prompt('New group label:', groupCards[0].group_label || '');
                                if (lbl !== null) await api.cards.updateGroupLabel(groupId, lbl, sessionId);
                                loadSession();
                              }}>✏️</button>
                          )}
                        </div>
                      )}
                      <div className="flex-col gap-2">
                        {groupCards.map(c => (
                          <RetroCard key={c.id} card={c} phase={phase} isFacilitator={isFacilitator}
                            myParticipantId={myParticipantId} isRevealed={c.is_revealed} isLocked={isLocked}
                            selectedForMerge={selectedCards.has(c.id)}
                            onSelectForMerge={toggleMergeCard}
                            onUpdated={updated => setCards(prev => prev.map(x => x.id === updated.id ? updated : x))}
                            onDeleted={id => setCards(prev => prev.filter(x => x.id !== id))}
                          />
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* Ungrouped cards */}
                  <div className="flex-col gap-2">
                    {ungrouped.map(c => (
                      <RetroCard key={c.id} card={c} phase={phase} isFacilitator={isFacilitator}
                        myParticipantId={myParticipantId} isRevealed={c.is_revealed} isLocked={isLocked}
                        selectedForMerge={selectedCards.has(c.id)}
                        onSelectForMerge={toggleMergeCard}
                        onUpdated={updated => setCards(prev => prev.map(x => x.id === updated.id ? updated : x))}
                        onDeleted={id => setCards(prev => prev.filter(x => x.id !== id))}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Hidden cards indicator */}
              {!isLocked && phaseCards.filter(c => c.participant_id !== myParticipantId).length > 0 && (
                <div className="toast toast-info mt-3">
                  <Eye size={14} /> {phaseCards.filter(c => c.participant_id !== myParticipantId).length} other card{phaseCards.filter(c => c.participant_id !== myParticipantId).length !== 1 ? 's' : ''} hidden until reveal
                </div>
              )}

              {/* FIX-11: Previous phases summary */}
              {(() => {
                const prevPhases = writingPhases.filter(p => phases.indexOf(p) < phases.indexOf(phase));
                const prevCards = cards.filter(c => prevPhases.includes(c.phase) && c.is_revealed);
                if (!prevCards.length) return null;
                return (
                  <PrevPhaseSummary cards={prevCards} phases={prevPhases}
                    isFacilitator={isFacilitator} myParticipantId={myParticipantId}
                    selectedCards={selectedCards} onSelectForMerge={toggleMergeCard}
                    onUpdated={updated => setCards(prev => prev.map(x => x.id === updated.id ? updated : x))}
                    onDeleted={id => setCards(prev => prev.filter(x => x.id !== id))}
                  />
                );
              })()}
            </div>
          )}

          {/* DISCUSSION, VOTING, COMPLETE phases */}
          {['discussion', 'voting', 'complete'].includes(phase) && (
            <div>
              {/* Board summary (all phases collapsed) */}
              <div className="card mb-4">
                <h3 style={{ fontSize: '1rem', marginBottom: 16 }}>Board Summary</h3>
                <div className="grid-3" style={{ gap: 12 }}>
                  {['start','stop','continue'].map(p => {
                    const pCards = cards.filter(c => c.phase === p && c.is_revealed);
                    const meta = PHASE_META[p];
                    return (
                      <div key={p} style={{ background: `color-mix(in srgb, ${meta.color} 8%, transparent)`, border: `1px solid color-mix(in srgb, ${meta.color} 30%, transparent)`, borderRadius: 'var(--radius-sm)', padding: '10px 12px' }}>
                        <div className="flex items-center gap-2 mb-2">
                          <span>{meta.emoji}</span>
                          <span className="text-xs font-bold" style={{ color: meta.color }}>{meta.label}</span>
                          <span className="badge badge-muted" style={{ marginLeft: 'auto' }}>{pCards.length}</span>
                        </div>
                        <div className="flex-col gap-1">
                          {pCards.map(c => (
                            <div key={c.id}>
                              {c.group_label && <span className="badge badge-purple" style={{ fontSize: 9, marginRight: 4 }}>{c.group_label}</span>}
                              <span className="text-xs" style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>• {c.content}</span>
                              {c.author_name && !c.is_anonymous && <span className="text-xs text-muted" style={{ marginLeft: 4, fontStyle: 'italic' }}>— {c.author_name}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Kudos */}
                {cards.filter(c => c.phase === 'kudos' && c.is_revealed).length > 0 && (
                  <div style={{ marginTop: 12, background: 'var(--yellow-dim)', border: '1px solid rgba(245,200,66,0.3)', borderRadius: 'var(--radius-sm)', padding: '10px 12px' }}>
                    <div className="text-xs font-bold mb-2" style={{ color: 'var(--yellow)' }}>🏆 Kudos</div>
                    <div className="flex gap-2 flex-wrap">
                      {cards.filter(c => c.phase === 'kudos' && c.is_revealed).map(c => (
                        <div key={c.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '4px 10px', fontSize: 12 }}>
                          {c.sticker && <span style={{ marginRight: 4 }}>{c.sticker}</span>}
                          {c.content}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Action panel */}
              <ActionPanel
                sessionId={sessionId} teamId={session?.team_id} actions={actions} isFacilitator={isFacilitator}
                myParticipantId={myParticipantId} myVotes={myVotes} phase={phase}
                onActionsUpdated={setActions}
                onVotesUpdated={setMyVotes}
              />

              {/* Report export (complete phase) */}
              {phase === 'complete' && (
                <div className="card mt-4">
                  <h3 style={{ fontSize: '1rem', marginBottom: 12 }}>Export Report</h3>
                  <div className="flex gap-3">
                    <button className="btn btn-secondary" onClick={() => {
                      const analysis = localStorage.getItem(`retro_analysis_${sessionId}`) || '';
                      api.reports.downloadSessionPdf(sessionId, analysis);
                    }}>
                      <Download size={14} /> PDF Report
                    </button>
                    <button className="btn btn-secondary" onClick={() => api.reports.downloadCsv(sessionId)}>
                      <Download size={14} /> CSV
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div style={{ position: 'sticky', top: 80 }}>
          {/* Participants */}
          <div className="card mb-3">
            <div className="flex items-center justify-between" style={{ cursor: 'pointer', marginBottom: showParticipants ? 12 : 0 }} onClick={() => setShowParticipants(s => !s)}>
              <div className="flex items-center gap-2"><Users size={14} /><span className="font-bold text-sm">Team ({participants.filter(p => p.is_active).length})</span></div>
              {showParticipants ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </div>
            {showParticipants && (
              <div className="flex-col">
                {[...participants.filter(p => p.is_active)].sort((a, b) => {
                  if (a.id === myParticipantId) return -1;
                  if (b.id === myParticipantId) return 1;
                  return 0;
                }).map(p => {
                  const isMe = p.id === myParticipantId;
                  return (
                  <div key={p.id} className="flex items-center gap-2" style={{ padding: '6px 0', borderRadius: 6, background: isMe ? 'var(--accent-dim)' : 'transparent', paddingLeft: isMe ? 6 : 0, outline: isMe ? '1px solid var(--accent)' : 'none' }}>
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--accent-dim)', border: '2px solid var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'var(--accent)', flexShrink: 0 }}>
                      {p.display_name.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="text-sm truncate">{p.display_name}</div>
                      {p.is_facilitator && <div className="text-xs" style={{ color: 'var(--accent)' }}>Facilitator</div>}
                    </div>
                    {isFacilitator && !p.is_facilitator && p.jira_id && (
                      <button className="btn btn-ghost btn-icon btn-sm" title="Make facilitator" onClick={() => transferFacilitator(p.id)}>
                        <UserCheck size={12} />
                      </button>
                    )}
                    {isMe && <span className="text-xs" style={{ color: 'var(--accent)', marginLeft: 'auto', paddingRight: 4 }}>you</span>}
                    {phase === 'voting' && !isMe && (() => {
                      const pVotes = allVotes.filter(v => v.participant_id === p.id).length;
                      return pVotes > 0 ? <span className="badge badge-purple" style={{ fontSize: 9 }}>{pVotes}/3</span> : <span className="text-xs text-muted" style={{ fontSize: 9 }}>—</span>;
                    })()}
                  </div>
                  );
                })}
                <div className="divider" />
                {myParticipantId && (() => {
                  const me = participants.find(p => p.id === myParticipantId);
                  if (!me) return null;
                  return editingName ? (
                    <div className="flex gap-1 mt-1">
                      <input className="input" value={nameInput} onChange={e => setNameInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleNameSave(); if (e.key === 'Escape') setEditingName(false); }}
                        placeholder="Your name" style={{ flex: 1, fontSize: 12 }} autoFocus />
                      <button className="btn btn-success btn-sm" onClick={handleNameSave}>✓</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditingName(false)}>✕</button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 mt-1 text-xs text-muted">
                      <span>👤 You · {me.display_name}</span>
                      <button className="btn btn-ghost btn-icon" style={{ padding: '2px 4px' }} title="Edit your name"
                        onClick={() => { setNameInput(me.display_name); setEditingName(true); }}>✏️</button>
                    </div>
                  );
                })()}
                <button className="btn btn-ghost btn-sm w-full" onClick={copyLink}><Copy size={12} /> {copied ? 'Copied!' : 'Copy invite link'}</button>
              </div>
            )}
          </div>

          {/* Re-estimate panel toggle */}
          {isFacilitator && session.teams?.jira_project_key && (
            <div className="card mb-3">
              <div className="flex items-center justify-between" style={{ cursor: 'pointer' }} onClick={() => setShowReEstimate(s => !s)}>
                <div className="flex items-center gap-2">
                  <AlertTriangle size={14} style={{ color: 'var(--yellow)' }} />
                  <span className="font-bold text-sm" style={{ color: 'var(--yellow)' }}>Re-estimate</span>
                </div>
                {showReEstimate ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </div>
              {showReEstimate && (
                <div style={{ marginTop: 12 }}>
                  <ReEstimatePanel
                    teamId={session.team_id}
                    jiraProjectKey={session.teams?.jira_project_key}
                    cloudName={user?.cloudName}
                  />
                </div>
              )}
            </div>
          )}

          {/* Phase guide */}
          <div className="card">
            <div className="text-xs font-bold text-muted mb-2" style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>Phase Guide</div>
            <div className="flex-col gap-1">
              {phases.filter(p => p !== 'lobby').map(p => {
                const meta = PHASE_META[p] || { label: p, emoji: '📋', color: 'var(--text-muted)' };
                const isCurrent = p === phase;
                return (
                  <div key={p} style={{ padding: '4px 8px', borderRadius: 6, background: isCurrent ? `color-mix(in srgb, ${meta.color} 12%, transparent)` : 'transparent', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 12 }}>{meta.emoji}</span>
                    <span className="text-xs" style={{ color: isCurrent ? meta.color : 'var(--text-muted)', fontWeight: isCurrent ? 700 : 400 }}>{meta.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 300 }}>
          <div className="toast toast-error">{error}<button className="btn btn-ghost btn-sm" onClick={() => setError('')}>✕</button></div>
        </div>
      )}

      {isFacilitator && <FacilitatorNotes sessionId={sessionId} />}
      <TutorialModal isFacilitator={isFacilitator} onClose={() => {}} />
    </div>
  );
}
